import { useRef } from 'react';
import type { ReactElement } from 'react';
import type { GameState, InstanceId } from '@cards/engine';
import { registry } from '@cards/cards';
import { cardInfoDataFromDefinition } from './CardInfoCard.js';
import { CatalogCard } from './CatalogCard.js';
import { Alert, Badge, Button, Modal, PanelHeader } from './kit/index.js';
import { cardName } from './locale.js';

export interface DeckViewerProps {
  state: GameState;
  instanceIds: readonly InstanceId[];
  onClose: () => void;
}

interface DeckGroup {
  defId: string;
  count: number;
}

export function DeckViewer({ state, instanceIds, onClose }: DeckViewerProps): ReactElement {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const grouped = new Map<string, DeckGroup>();
  let hiddenCount = 0;

  for (const instanceId of instanceIds) {
    const instance = state.instances[instanceId];
    if (instance === undefined || registry.get(instance.defId) === undefined) {
      hiddenCount += 1;
      continue;
    }
    const previous = grouped.get(instance.defId);
    if (previous === undefined) grouped.set(instance.defId, { defId: instance.defId, count: 1 });
    else previous.count += 1;
  }

  const groups = [...grouped.values()].sort((left, right) => {
    const leftDefinition = registry.get(left.defId);
    const rightDefinition = registry.get(right.defId);
    return (leftDefinition?.cost ?? Number.MAX_SAFE_INTEGER) - (rightDefinition?.cost ?? Number.MAX_SAFE_INTEGER)
      || cardName(left.defId).localeCompare(cardName(right.defId), 'ru');
  });

  return (
    <Modal
      as="section"
      layer="viewer"
      overlayClassName="cards-deck-viewer-overlay"
      overlayTestId="deck-viewer-overlay"
      className="cards-deck-viewer"
      testId="deck-viewer"
      labelledBy="deck-viewer-title"
      describedBy="deck-viewer-description"
      initialFocusRef={closeButtonRef}
      onClose={onClose}
    >
      <PanelHeader
        className="cards-deck-viewer-header"
        title={`ВАША КОЛОДА · ${instanceIds.length}`}
        headingLevel={2}
        headingId="deck-viewer-title"
        description="Показаны оставшиеся карты, сгруппированные по видам. Порядок добора скрыт."
        actions={(
          <Button
            variant="ghost"
            size="sm"
            ref={closeButtonRef}
            className="cards-deck-viewer-close"
            data-testid="deck-viewer-close"
            onClick={onClose}
            aria-label="Закрыть просмотр колоды"
          >
            ×
          </Button>
        )}
      />

      {groups.length === 0 && hiddenCount === 0 ? (
        <div className="cards-deck-viewer-empty" data-testid="deck-viewer-empty">
          Колода пуста
        </div>
      ) : (
        <>
          <div
            className="cards-deck-viewer-cards cards-catalog-grid cards-catalog-grid-mobile-portrait"
            data-testid="deck-viewer-cards"
          >
            {groups.map(({ defId, count }) => {
              const info = cardInfoDataFromDefinition(defId)!;
              return (
                <div
                  className="cards-deck-viewer-card"
                  key={defId}
                  data-testid={`deck-viewer-card-${defId}`}
                >
                  <CatalogCard
                    data={info}
                    testId={`deck-viewer-card-${defId}-info`}
                    footer={(
                      <Badge
                        tone="success"
                        className="cards-deck-viewer-count"
                        aria-label={`Копий карты ${cardName(defId)}: ${count}`}
                      >
                        ×{count}
                      </Badge>
                    )}
                  />
                </div>
              );
            })}
          </div>
          {hiddenCount > 0 ? (
            <Alert tone="warning" role="status" className="cards-deck-viewer-hidden" data-testid="deck-viewer-hidden">
              Не удалось прочитать карт: {hiddenCount}
            </Alert>
          ) : null}
        </>
      )}
    </Modal>
  );
}
